import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Timeline,
  Popover,
  Empty,
  Flex,
  Spin,
  Tag,
  Modal,
  Form,
  Input,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  RightOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { WorkRequestData, WorkRequestLine, WorkLineSource } from "./types";
import workRequestApis from "../../api/WorkOrderManagement/WorkRequestApis";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { formatDecimal } from "../../utils/Utils";
import { message } from "antd";
import SignatureModal from "../components/SignatureModal";
import ImageUploadCropper from "../components/ImageUploadCropper";
import { useSelector } from "react-redux";

const { Panel } = AntCollapse;

const WorkRequestView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const searchParams = new URLSearchParams(routerLocation.search);
  const flowType = searchParams.get("flowType");
  const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
  const [workRequest, setWorkRequest] = useState<WorkRequestData | null>(null);
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [disableApprove, setDisableApprove] = useState(false);
  const [disableReject, setDisableReject] = useState(false);
  const [shareOptionsToggle, setShareOptionsToggle] = useState(false);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);

  // Mock current user permissions
  const [allowedActions] = useState({
    view: 1,
    edit: 1,
    approve: 1,
    reject: 1,
  });

  const canApprove =
    allowedActions.approve === 1
    && workRequest?.approvalStatus === "Pending";

  const [form] = Form.useForm();
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const [currentUserId, setCurrentUserId] = useState<any>(null);
  const [approverIds, setApproverIds] = useState<any>([]);

  const [signature, setSignature] = useState({
    isActive: false,
    label: "Authorised Signatory",
    image: null as string | null,
  });

  const [showSignatureModel, setShowSignatureModel] = useState(false);
  const [isModalApproveVisible, setIsModalApproveVisible] = useState(false);
  const [approveRecord, setApproveRecord] = useState<any>(null);
  const [activeSignatureContext, setActiveSignatureContext] = useState<
    "APPROVE" | "REJECT" | null
  >(null);
  const [isStrictMode, setIsStrictMode] = useState<boolean>(false);
  const [isModalRejectedVisible, setIsModalRejectedVisible] = useState(false);
  const [showActionModelVisible, setShowActionModelVisible] = useState(false);
  const [rejectRecord, setRejectRecord] = useState<any>(null);

  useEffect(() => {
    if (currentUser) {
      const { id } = currentUser;
      setCurrentUserId(id);
    }
  }, [currentUser]);

  useEffect(() => {
    if (id) {
      loadWorkRequestData(id);
    }
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [id]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const loadWorkRequestData = async (workRequestId: string) => {
    try {
      setLoading(true);

      if (!workRequestId) {
        message.error("Work Request ID is required");
        setLoading(false);
        return;
      }

      console.log("Fetching Work Request with ID:", workRequestId);

      // Call the actual API
      const response: any = await workRequestApis.getWorkRequestById(
        workRequestId
      );

      const isStrict =
        response?.data?.workRequest?.isStrictApproval === true ||
        response?.data?.workRequest?.isStrictApproval === 1;
      setIsStrictMode(isStrict);

      if (response.data?.workRequestApprovals) {
        const processedApprovals = response.data.workRequestApprovals.map((approval: any) => ({
          ...approval,
          level: approval.level?.toLowerCase() || approval.level,
          status: approval.status?.toLowerCase() || approval.status
        }));
        response.data.workRequestApprovals = processedApprovals;

        // Filter pending approvals
        let ids = processedApprovals
          ?.filter((item: any) => item?.status?.toLowerCase() !== "approved")
          ?.map((item: any) => item?.userId);
        setApproverIds(ids);
      }

      if (
        response &&
        response.status &&
        response.data &&
        response.data.workRequest
      ) {
        const apiData = response.data.workRequest;
        console.log("API Response:", apiData);

        // Map approvals
        const mappedApprovals =
          apiData.workRequestApprovals?.map((approval: any) => ({
            id: approval.workRequestApprovalId?.toString() || "",
            userId: approval.userId || "",
            approverName: approval.userName || "",
            approverRole: approval.roleName || "",
            level: approval.level || "",
            status: approval.status || "",
            approvalStage: approval.approvalStage || "",
            reason: approval.reason || "",
            createdAt: approval.createdAt || "",
          })) || [];

        // Map work request lines with proper structure
        const mappedWorkRequestLines =
          apiData.workRequestLines?.map((line: any, index: number) => ({
            id: line.workRequestLineId?.toString() || index.toString(),
            taskTitle: line.taskTitle || "",
            taskDescription: line.taskDescription || "",
            category: line.workCategoryName || "",
            subcategory: line.workSubCategoryName || "",
            quantity: line.quantity || 0,
            uom: line.uomName || "",
            source: line.sourceType || "Project Tasks",
            sourceId: line.sourceId?.toString() || "",
            workCategoryId: line.workCategoryId?.toString() || "",
            uomId: line.uomId?.toString() || "",
            // Material BOQ allocation from task.taskBoqs
            materialAllocation:
              line.task?.taskBoqs?.map((boq: any) => ({
                materialId: boq.materialId?.toString() || "",
                materialName: boq.materialName || "",
                quantity: parseFloat(boq.allotedQuantity || "0"),
                category: boq.categoryName || "",
                uom: boq.uomName || "",
                activityAllotedQuantity: parseFloat(
                  boq.activityAllotedQuantity || "0"
                ),
                boqTargetNumber: boq.boqTargetNumber || "",
              })) || [],
            // Additional task information
            task: line.task
              ? {
                taskId: line.task.taskId,
                taskSequenceId: line.task.taskSequenceId,
                taskName: line.task.taskName,
                taskDescription: line.task.taskDescription,
                taskStatus: line.task.taskStatus,
              }
              : null,
            documents: [], // Not provided in API response
            plannedStartDate: null, // Not provided in API response for lines
            plannedEndDate: null, // Not provided in API response for lines
            assignedTower: null, // Not provided in API response
            assignedVilla: null, // Not provided in API response
          })) || [];

        // Map API response to WorkRequestData interface
        const mappedWorkRequest: any = {
          id: apiData.workRequestId?.toString() || workRequestId,
          workRequestNumber: apiData.workRequestSequenceId || "",
          workRequestTitle: apiData.workRequestTitle || "",
          description: apiData.description || "",
          siteName: apiData.siteName || "",
          projectName: apiData.projectTitle || "",
          requestFor: apiData.requestFor || "",
          plannedStartDate: apiData.plannedStartDate || "",
          plannedEndDate: apiData.plannedEndDate || "",
          status: apiData.status || "Draft",
          approvalStatus: apiData.approvalStatus || "Draft",
          approvalRequired: apiData.approvalRequired || false,
          sourceType: apiData.sourceType || "Project Tasks",
          createdBy: apiData.createdByName || "Unknown",
          createdAt: apiData.createdAt || new Date().toISOString(),
          updatedAt: apiData.updatedAt || apiData.createdAt,
          totalLines:
            apiData.workRequestLinesCount || mappedWorkRequestLines.length,
          workLines: mappedWorkRequestLines,
          // Additional mapped fields
          siteId: apiData.siteId,
          projectId: apiData.projectId,
          approvals: apiData.workRequestApprovals || [],
          workRequestApprovals: mappedApprovals,
        };

        setWorkRequest(mappedWorkRequest);
        message.success("Work Request details loaded successfully");
      } else {
        throw new Error(
          response?.message || "Failed to fetch Work Request details"
        );
      }
    } catch (error: any) {
      console.error("Error loading work request:", error);
      message.error("Failed to load work request data");
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.message ||
          "Failed to load work request data. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const parseLevelNum = (level: any) => {
    if (!level && level !== 0) return NaN;
    const str = String(level).toLowerCase();

    // Handle various level formats: level1, Level2, L1, L2, etc.
    if (str.includes('level')) {
      const match = str.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    } else if (str.startsWith('l')) {
      const match = str.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    }
    return NaN;
  };

  const hasUserAlreadyApproved = workRequest?.approvals?.some(
    (a: any) =>
      a.userId === currentUser?.id &&
      a.status?.toUpperCase() === "APPROVED"
  );

  const canUserApproveReject = useCallback(() => {
    // 1. Admin can Approve/Reject anytime - Buttons are always ENABLED
    if (currentUser?.genericRole?.toLowerCase() === "admin") {
      return true;
    }

    const approvals = workRequest?.approvals || [];

    // Find current user's approval entry
    const userApproval = approvals.find(
      (approval: any) => approval.userId === currentUserId
    );

    if (!userApproval || userApproval.status?.toLowerCase() !== "pending") {
      return false;
    }

    const userLevelNum = parseLevelNum(userApproval.level ?? currentUser?.approvalLevel);

    // 2. Strict = 0 (Flexible mode)
    // Anyone in the approval list can approve at any time
    if (isStrictMode === false) {
      // In flexible mode, just check if user is in the approval list and pending
      return true;
    }

    // 3. Strict = 1 (Sequential mode)
    // Must follow the order. Level 2 cannot see buttons until Level 1 is "Approved"
    if (isStrictMode === true) {
      // Get all pending approvals sorted by level
      const pendingApprovals = approvals
        .filter((approval: any) => approval.status?.toLowerCase() === "pending")
        .sort((a: any, b: any) => {
          const aNum = parseLevelNum(a.level);
          const bNum = parseLevelNum(b.level);
          if (isNaN(aNum) || isNaN(bNum)) return 0;
          return aNum - bNum;
        });

      // If there are no pending approvals, user can't approve
      if (pendingApprovals.length === 0) return false;

      // Get the first pending approval (lowest level)
      const firstPending = pendingApprovals[0];
      const firstPendingLevelNum = parseLevelNum(firstPending.level);

      // Check if user is the first pending approver
      return userApproval.userId === firstPending.userId &&
        userLevelNum === firstPendingLevelNum;
    }

    return false;
  }, [currentUser, currentUserId, workRequest?.approvals, isStrictMode]);

  const getPendingStatusMessage = () => {
    const approvals = workRequest?.approvals || [];
    const pendingApprovals = approvals
      .filter((approval: any) => approval.status?.toLowerCase() === "pending")
      .sort((a: any, b: any) => {
        const aNum = parseLevelNum(a.level);
        const bNum = parseLevelNum(b.level);
        if (isNaN(aNum) || isNaN(bNum)) return 0;
        return aNum - bNum;
      });

    if (pendingApprovals?.length > 0) {
      if (!isStrictMode) {
        // Flexible mode - any approver can approve
        const approverNames = pendingApprovals.map((approval: any) => approval.userName).join(", ");
        return `Status is pending. Any of these approvers can approve: ${approverNames}`;
      } else {
        // Strict mode - must follow sequence
        const nextApprover = pendingApprovals[0];
        const lvl = parseLevelNum(nextApprover.level);
        const levelText = !isNaN(lvl) ? `Level ${lvl}` : nextApprover.roleName || "";
        return `Status is pending. Waiting for ${nextApprover.userName} (${levelText}) to approve first.`;
      }
    }

    return "Status is pending by other approvers";
  };

  const handleEdit = () => {
    if (workRequest) {
      const searchParams = new URLSearchParams(routerLocation.search);
      const flowType = searchParams.get("flowType");
      const flowTypeQuery = flowType ? `&flowType=${flowType}` : "";
      navigate(`/work-request-creation?edit=${workRequest.id}${flowTypeQuery}`);
    }
  };

  const handleDuplicate = () => {
    if (workRequest) {
      const searchParams = new URLSearchParams(routerLocation.search);
      const flowType = searchParams.get("flowType");
      const flowTypeQuery = flowType ? `&flowType=${flowType}` : "";
      navigate(
        `/work-request-creation?duplicate=${workRequest.id}${flowTypeQuery}`
      );
    }
  };

  const handleDelete = () => {
    if (!workRequest) return;

    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Note: Add delete API when available
          message.info(
            "Delete functionality will be implemented when API is available"
          );
        } catch (error: any) {
          console.error("Error deleting work request:", error);
          Swal.fire("Error!", "Failed to delete work request.", "error");
        }
      }
    });
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Draft: "secondary",
      "Pending Approval": "warning",
      Approved: "success",
      Rejected: "danger",
      "In Progress": "info",
      Completed: "success",
    };
    return statusColors[status] || "secondary";
  };

  const getSourceColor = (source: WorkLineSource) => {
    const colors = {
      [WorkLineSource.ProjectManagement]: "blue",
      [WorkLineSource.WorkBOQ]: "green",
      [WorkLineSource.NewWorkLine]: "orange",
    };
    return colors[source] || "default";
  };

  // Material BOQ expandable row component for view
  const expandedRowRender = (record: WorkRequestLine) => {
    const materialAllocation = (record as any).materialAllocation;

    if (!materialAllocation || materialAllocation.length === 0) {
      return null;
    }

    return (
      <div className="expandableBody">
        <div style={{ flex: 1, textAlign: "left" }}>
          <div className="mb-3">
            <strong>
              📦 Material BOQ Allocation ({materialAllocation.length} items):
            </strong>
            <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
              {materialAllocation.map((material: any, index: number) => (
                <li key={index} style={{ marginBottom: "5px" }}>
                  <strong>{material.materialName}</strong> - Qty:{" "}
                  {material.quantity} {material.uom}
                  {material.category && (
                    <span style={{ marginLeft: "10px" }}>
                      <Tag color="blue">{material.category}</Tag>
                    </span>
                  )}
                  {material.materialId && (
                    <span
                      style={{
                        marginLeft: "5px",
                        color: "#666",
                        fontSize: "12px",
                      }}
                    >
                      (ID: {material.materialId})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Work Lines Table Columns
  const workLinesColumns: TableColumnsType<any> = [
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
      fixed: "left",
      render: (text: string, record: WorkRequestLine) => (
        <div>
          <div className="fw-bold">{text}</div>
          {record.taskDescription && (
            <small className="text-muted">{record.taskDescription}</small>
          )}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: (text: string, record: WorkRequestLine) => (
        <div>
          <div>{text}</div>
          {record.subcategory && (
            <small className="text-muted">{record.subcategory}</small>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 130,
      render: (quantity: number, record: WorkRequestLine) => {
        const availableQty = (record as any).availableQuantity || quantity;
        const requestedQty = (record as any).requestedQuantity || quantity;

        if (availableQty !== requestedQty) {
          return (
            <div>
              <div className="text-muted small">
                Available: {availableQty.toLocaleString()}
              </div>
              <div>
                <strong>Requested: {requestedQty.toLocaleString()}</strong>{" "}
                {record.uom}
              </div>
            </div>
          );
        }

        return `${formatDecimal(quantity)} ${record?.uom}`;
      },
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 80,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 140,
      render: (source: string, record: WorkRequestLine) => {
        const materialAllocation = (record as any).materialAllocation;
        const hasMaterialBOQ =
          materialAllocation && materialAllocation.length > 0;

        return (
          <div>
            <Tag color={getSourceColor(source as WorkLineSource)}>{source}</Tag>
            {hasMaterialBOQ && (
              <div style={{ marginTop: 4 }}>
                <Tag color="purple" style={{ fontSize: "10px" }}>
                  📦 Material BOQ
                </Tag>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Location",
      key: "location",
      width: 120,
      render: (_: any, record: WorkRequestLine) => {
        const location = [];
        if (record.assignedTower) location.push(record.assignedTower);
        if (record.assignedVilla) location.push(record.assignedVilla);
        return location.join(" / ") || "-";
      },
    },
    {
      title: "Planned Dates",
      key: "plannedDates",
      width: 150,
      render: (_: any, record: WorkRequestLine) => (
        <div>
          {record.plannedStartDate && (
            <div>
              <small>Start: {record.plannedStartDate}</small>
            </div>
          )}
          {record.plannedEndDate && (
            <div>
              <small>End: {record.plannedEndDate}</small>
            </div>
          )}
          {!record.plannedStartDate && !record.plannedEndDate && "-"}
        </div>
      ),
    },
    {
      title: "Documents",
      key: "documents",
      width: 100,
      render: (text, record) =>
        record.documents?.length > 0 ? (
          <CheckCircleOutlined style={{ color: "#28a745" }} />
        ) : (
          <CloseCircleOutlined style={{ color: "#dc3545" }} />
        ),
    },
  ];

  const getStatusMapper = (status: string) => {
    const statusMap: { [key: string]: { title: string; color: string } } = {
      Draft: { title: "Draft", color: "secondary" },
      "Pending Approval": { title: "Pending Approval", color: "warning" },
      Approved: { title: "Approved", color: "success" },
      Rejected: { title: "Rejected", color: "danger" },
      "In Progress": { title: "In Progress", color: "info" },
      Completed: { title: "Completed", color: "success" },
    };
    return statusMap[status] || { title: status, color: "secondary" };
  };

  if (loading) {
    return (
      <>
        <Head title="Work Request Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!workRequest) {
    return (
      <>
        <Head title="Work Request Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Work Request not found" />
          </div>
        </Content>
      </>
    );
  }

  const statusInfo = getStatusMapper(workRequest.status);

  const handleCancel = () => {
    setShowActionModelVisible(false);
    setIsModalRejectedVisible(false);
    form.resetFields();
  };

  const dataURLToFile = (dataUrl: string, fileName: string): File => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };

  const handleApprovalAction = async (
    workRequestId: number,
    action: "Approved" | "Rejected",
    reason?: string,
    files: File[] = []
  ) => {
    try {
      const approvalData = {
        workRequestId,
        action,
        reason: reason || (action === "Approved"
          ? "Work request approved for further processing"
          : "Work request rejected"),
      };

      const response: any = await workRequestApis.workRequestApprovalAction(
        approvalData,
        files
      );

      if (response && response.status) {
        const successMessage = action === "Approved"
          ? "Work Request approved successfully!"
          : "Work Request rejected successfully!";

        message.success(successMessage);
        // Reload the data to reflect the new status
        await loadWorkRequestData(id!);
      } else {
        throw new Error(
          response?.message || `Failed to ${action.toLowerCase()} Work Request`
        );
      }
    } catch (error) {
      console.error(`Error ${action.toLowerCase()}ing Work Request:`, error);
      message.error(`Failed to ${action.toLowerCase()} Work Request. Please try again.`);
    } finally {
      setSignature({ isActive: false, label: "Authorised Signatory", image: null });
      if (action === "Rejected") {
        form.resetFields();
      }
    }
  };

  const openApproveConfirmSwal = () => {
    if (!signature.image) return;

    setIsModalApproveVisible(false);

    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve it!",
      cancelButtonText: "No, keep it",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const file = dataURLToFile(signature.image as string, "signature.png");
        await handleApprovalAction(parseInt(approveRecord), "Approved", undefined, [file]);
      } else {
        setIsModalApproveVisible(true);
      }
    });
  };

  const openRejectConfirmSwal = async () => {
    try {
      await form.validateFields();

      if (!signature.image) {
        Swal.fire({
          icon: "info",
          title: "Signature Required",
          text: "Please add your signature before rejecting.",
        });
        return;
      }

      setIsModalRejectedVisible(false);

      Swal.fire({
        title: "Confirm Rejection?",
        text: "This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Reject it!",
        cancelButtonText: "Cancel",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const values = form.getFieldsValue();
          const file = dataURLToFile(signature.image as string, "signature.png");
          await handleApprovalAction(
            parseInt(rejectRecord),
            "Rejected",
            values.reason,
            [file]
          );
        } else {
          setIsModalRejectedVisible(true);
        }
      });
    } catch (e) {
      console.log("Validation failed:", e);
    }
  };

  const ApprovalTimelineContent = () => {
    const approvalsData = workRequest.approvals;
    if (!approvalsData || approvalsData?.length === 0)
      return <Badge color="outline-danger">No</Badge>;
    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status?.toLowerCase() == "approved";
              const isRejected = approval.status?.toLowerCase() == "rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.userName}
                  <div className="designation">{approval?.roleName}</div>
                  {approval?.actionDate && (
                    <div className="designation">
                      {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                  {approval.approvalStage && (
                    <div className="text-muted small">
                      <Tag
                        color="blue"
                        style={{ fontSize: "10px" }}
                      >
                        {approval.approvalStage}
                      </Tag>
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer"
        }}>
          <span
            style={{
              color: "#2263b3",
              // textDecoration: "underline",
            }}
          >
            <Badge color="outline-success">Yes</Badge>
          </span>

          {isStrictMode && (
            <Badge
              color="warning"
              style={{
                fontSize: "11px",
                cursor: "pointer",
                marginLeft: "4px"
              }}
              title="Sequential approvals required, Approvals must follow the sequence"
            >
              Strict
            </Badge>
          )}
        </div>
      </Popover>
    );
  };

  return (
    <>
      <Head title={`Work Request - ${workRequest.workRequestNumber}`} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {workRequest.workRequestNumber} | {statusInfo.title}
                      </span>
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    {/* Action buttons based on status */}
                    {workRequest.status === "Draft" && (
                      <div className="actions clearfix">
                        <ul
                          className="m-auto d-flex align-items-center justify-content-center p-0"
                          style={{ flexDirection: "row-reverse" }}
                        >
                          <li className="pt-0 pb-0">
                            <Button color="primary" onClick={handleEdit}>
                              <EditOutlined /> Edit
                            </Button>
                          </li>
                          {/* <li className="pt-0 pb-0">
                            <Button color="secondary" onClick={handleDuplicate}>
                              <CopyOutlined /> Duplicate
                            </Button>
                          </li> */}
                          <li className="pt-0 pb-0">
                            <Button color="danger" onClick={handleDelete}>
                              <DeleteOutlined /> Delete
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )}

                    <div className="actions clearfix">
                      <ul
                        className="m-auto d-flex align-items-center justify-content-center p-0"
                        style={{ flexDirection: "row-reverse" }}
                      >
                        <li className="pt-0 pb-0">
                          <Dropdown
                            isOpen={shareOptionsToggle}
                            toggle={() => {
                              setShareOptionsToggle(!shareOptionsToggle);
                            }}
                          >
                            <DropdownToggle className="btn btn-white btn-outline-light">
                              <Icon name="share"></Icon>
                              <span>Share</span>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => { }}>
                                <Icon name="mail"></Icon>
                                <span>Send Mail</span>
                              </DropdownItem>
                              <DropdownItem onClick={() => { }}>
                                <Icon name="download"></Icon>
                                <span>Export PDF</span>
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </li>

                        <li className="pt-0 pb-0 ms-2">
                          <Dropdown
                            isOpen={actionsDropdownOpen}
                            toggle={() => setActionsDropdownOpen(!actionsDropdownOpen)}
                          >
                            <DropdownToggle
                              color="primary"
                              className="btn-icon"
                              style={{ padding: "8px 12px" }}
                            >
                              <MoreOutlined style={{ fontSize: "18px" }} />
                            </DropdownToggle>
                            <DropdownMenu end>
                              {canApprove && !hasUserAlreadyApproved && (
                                <>
                                  {canUserApproveReject() ? (
                                    <>
                                      <DropdownItem
                                        onClick={() => {
                                          setApproveRecord(workRequest.id);
                                          setActiveSignatureContext("APPROVE");
                                          setIsModalApproveVisible(true);
                                        }}
                                        disabled={disableApprove}
                                        className="text-success"
                                      >
                                        <CheckOutlined className="me-2" />
                                        {disableApprove ? "Approve..." : "Approve"}
                                      </DropdownItem>
                                      <DropdownItem
                                        onClick={() => {
                                          setRejectRecord(workRequest.id);
                                          setActiveSignatureContext("REJECT");
                                          setIsModalRejectedVisible(true);
                                          form.resetFields();
                                        }}
                                        disabled={disableReject}
                                        className="text-danger"
                                      >
                                        <CloseOutlined className="me-2" />
                                        {disableReject ? "Reject..." : "Reject"}
                                      </DropdownItem>
                                    </>
                                  ) : (
                                    <>
                                      {/* APPROVE (Disabled) */}
                                      <DropdownItem
                                        style={{
                                          cursor: "not-allowed",
                                          opacity: 0.6,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          gap: "8px",
                                        }}
                                        className="text-success"
                                        onClick={(e) => e.preventDefault()}
                                      >
                                        <div className="d-flex align-items-center gap-2">
                                          <CheckOutlined />
                                          <span>Approve</span>
                                        </div>

                                        <Popover
                                          content={
                                            <div style={{ padding: "8px", maxWidth: "300px" }}>
                                              <p style={{ margin: 0 }}>{getPendingStatusMessage()}</p>
                                            </div>
                                          }
                                          title="Approval Status"
                                          trigger="hover"
                                        >
                                          <InfoCircleOutlined
                                            style={{
                                              fontSize: "16px",
                                              cursor: "pointer",
                                              color: "#2263b3",
                                            }}
                                          />
                                        </Popover>
                                      </DropdownItem>

                                      {/* REJECT (Disabled) */}
                                      <DropdownItem
                                        style={{
                                          cursor: "not-allowed",
                                          opacity: 0.6,
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                        className="text-danger"
                                        onClick={(e) => e.preventDefault()}
                                      >
                                        <div
                                          className="d-flex align-items-center"
                                          style={{ gap: "13px" }}
                                        >
                                          <CloseOutlined />
                                          <span>Reject</span>
                                        </div>
                                      </DropdownItem>
                                    </>
                                  )}
                                </>
                              )}
                            </DropdownMenu>
                          </Dropdown>
                        </li>

                        <li className="pt-0 pb-0">
                          <Button
                            className="bg-white"
                            outline
                            color="light"
                            onClick={() => {
                              const searchParams = new URLSearchParams(
                                routerLocation.search
                              );
                              const flowType = searchParams.get("flowType");
                              const flowTypeQuery = flowType
                                ? `?flowType=${flowType}`
                                : "";
                              navigate(`/work-request-list${flowTypeQuery}`);
                            }}
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${sm ? "active" : ""
                        }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <Alert
                color={"danger"}
                isOpen={workRequest.status === "Rejected"}
              >
                <strong>Rejected: </strong>
                {workRequest.approvals?.find((approval: any) =>
                  approval.status?.toLowerCase() === "rejected"
                )?.reason || "No reason provided"}
              </Alert>

              <div className="content clearfix">
                <AntCollapse defaultActiveKey={["1", "2"]}>
                  <Panel
                    header={
                      <div className="d-flex align-items-center justify-content-between">
                        <>General Information</>
                      </div>
                    }
                    key="1"
                  >
                    <Col className="justify-start w-100 flex-wrap mt-3 p-3">
                      <div className="w-30 border-end">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Request #</div>
                          <div className="data-value w-70">
                            {workRequest.workRequestNumber || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Site</div>
                          <div className="data-value w-70">
                            {workRequest.siteName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Project</div>
                          <div className="data-value w-70">
                            {workRequest.projectName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Request For</div>
                          <div className="data-value w-70">
                            <Badge color="outline-primary">
                              {workRequest.requestFor || "--"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="w-30 border-end paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-40">Status</div>
                          <div className="data-value w-70">
                            <Badge color={`outline-${statusInfo.color}`}>
                              {statusInfo.title}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-40">Approval Required</div>
                          <div className="data-value w-70">
                            <ApprovalTimelineContent />
                          </div>
                        </div>

                        <div className="data-col align-center pt-1">
                          <div className="data-label w-40">Total Lines</div>
                          <div className="data-value w-70">
                            <Badge color="outline-info">
                              {workRequest.totalLines || 0}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-40">Created By</div>
                          <div className="data-value w-70">
                            {workRequest.createdBy || "--"}
                          </div>
                        </div>
                      </div>
                      <div className="w-30 paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Title</div>
                          <div className="data-value w-70">
                            <Popover
                              content={
                                <div>
                                  <p>{workRequest.workRequestTitle}</p>
                                </div>
                              }
                              title={"Work Request Title"}
                            >
                              {workRequest.workRequestTitle?.length > 30 ? (
                                <span
                                  style={{
                                    color: "#2263b3",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workRequest.workRequestTitle.substring(
                                    0,
                                    30
                                  )}
                                  ...
                                </span>
                              ) : (
                                workRequest.workRequestTitle || "--"
                              )}
                            </Popover>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Description</div>
                          <div className="data-value w-70">
                            <Popover
                              content={
                                <div>
                                  <p>
                                    {workRequest.description ||
                                      "No description provided"}
                                  </p>
                                </div>
                              }
                              title={"Description"}
                            >
                              {workRequest.description &&
                                workRequest.description.length > 30 ? (
                                <span
                                  style={{
                                    color: "#2263b3",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workRequest.description.substring(0, 30)}...
                                </span>
                              ) : (
                                workRequest.description || "--"
                              )}
                            </Popover>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Start Date</div>
                          <div className="data-value w-70">
                            {dayjs(workRequest.plannedStartDate).format(
                              "DD-MMM-YYYY"
                            )}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">End Date</div>
                          <div className="data-value w-70">
                            {dayjs(workRequest.plannedEndDate).format(
                              "DD-MMM-YYYY"
                            )}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Panel>

                  <Panel
                    header={`Work Request Lines (${workRequest.totalLines})`}
                    key="2"
                  >
                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        {!workRequest.workLines ||
                          workRequest.workLines.length === 0 ? (
                          <div className="text-center py-5">
                            <Empty description="No work request lines found" />
                          </div>
                        ) : (
                          <Table
                            className="customTable"
                            columns={workLinesColumns}
                            dataSource={workRequest.workLines}
                            scroll={{ x: 1000 }}
                            expandable={{
                              expandedRowRender,
                              expandIcon: ({ expanded, onExpand, record }) => {
                                const materialAllocation = (record as any)
                                  .materialAllocation;
                                const hasMaterialBOQ =
                                  materialAllocation &&
                                  materialAllocation.length > 0;

                                if (!hasMaterialBOQ) return null;

                                return expanded ? (
                                  <DownOutlined
                                    onClick={(e) => onExpand(record, e)}
                                    style={{
                                      color: "#1890ff",
                                      cursor: "pointer",
                                    }}
                                  />
                                ) : (
                                  <RightOutlined
                                    onClick={(e) => onExpand(record, e)}
                                    style={{
                                      color: "#1890ff",
                                      cursor: "pointer",
                                    }}
                                  />
                                );
                              },
                              rowExpandable: (record) => {
                                const materialAllocation = (record as any)
                                  .materialAllocation;
                                return (
                                  materialAllocation &&
                                  materialAllocation.length > 0
                                );
                              },
                            }}
                            bordered
                            size="small"
                            pagination={false}
                          />
                        )}
                      </div>

                      {workRequest.workLines?.length > 0 && (
                        <Row style={{ margin: "20px" }}>
                          <Col className="justify-end">
                            <div className="w-40">
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Total Work Lines</strong>
                                </div>
                                <span className="r_align">
                                  {workRequest.workLines.length}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Total Quantity</strong>
                                </div>
                                <span className="r_align">
                                  {formatDecimal(
                                    workRequest.workLines.reduce(
                                      (total: number, line: any) =>
                                        total + (Number(line.quantity) || 0),
                                      0
                                    )
                                  )}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Work Categories</strong>
                                </div>
                                <span className="r_align">
                                  {
                                    new Set(
                                      workRequest.workLines.map(
                                        (line: any) => line.category
                                      )
                                    ).size
                                  }
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </Panel>

                  {/* Status History Panel */}
                  <Panel header="Status History" key="3">
                    <Timeline>
                      <Timeline.Item color="blue">
                        <p>
                          <strong>Work Request Created</strong>
                        </p>
                        <p className="text-muted">
                          {dayjs(workRequest.createdAt).format(
                            "DD MMM YYYY HH:mm"
                          )}{" "}
                          by {workRequest.createdBy}
                        </p>
                      </Timeline.Item>

                      {workRequest.status === "Pending Approval" && (
                        <Timeline.Item color="orange">
                          <p>
                            <strong>Submitted for Approval</strong>
                          </p>
                          <p className="text-muted">
                            {dayjs(
                              workRequest.updatedAt || workRequest.createdAt
                            ).format("DD MMM YYYY HH:mm")}
                          </p>
                        </Timeline.Item>
                      )}

                      {workRequest.status === "Approved" && (
                        <Timeline.Item color="green">
                          <p>
                            <strong>Approved</strong>
                          </p>
                          <p className="text-muted">
                            {dayjs(
                              workRequest.updatedAt || workRequest.createdAt
                            ).format("DD MMM YYYY HH:mm")}
                          </p>
                          {(workRequest as any).approverComments && (
                            <p className="text-muted">
                              Comments: {(workRequest as any).approverComments}
                            </p>
                          )}
                        </Timeline.Item>
                      )}

                      {workRequest.status === "Rejected" && (
                        <Timeline.Item color="red">
                          <p>
                            <strong>Rejected</strong>
                          </p>
                          <p className="text-muted">
                            {dayjs(
                              workRequest.updatedAt || workRequest.createdAt
                            ).format("DD MMM YYYY HH:mm")}
                          </p>
                          {(workRequest as any).rejectionComments && (
                            <p className="text-muted">
                              Reason: {(workRequest as any).rejectionComments}
                            </p>
                          )}
                        </Timeline.Item>
                      )}
                    </Timeline>
                  </Panel>
                </AntCollapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>

      <Modal
        title="Approve Work Request"
        visible={isModalApproveVisible}
        onOk={() => {
          if (!signature.image) {
            Swal.fire({
              icon: "info",
              title: "Signature Required",
              text: "Please add your signature before approving.",
            });
            return;
          }
          openApproveConfirmSwal();
        }}

        onCancel={() => {
          setIsModalApproveVisible(false);
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span style={{ fontSize: 14, fontWeight: 600 }}>Signature</span>

            {signature.image && (
              <button
                type="button"
                onClick={() =>
                  setSignature({
                    isActive: true,
                    label: "Authorised Signatory",
                    image: null,
                  })
                }
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
              </button>
            )}
          </div>

          {signature.image ? (
            <>
              <div
                className="border mb-3 d-flex justify-content-center align-items-center"
                style={{
                  padding: 16,
                  borderRadius: 8,
                  background: "#fff",
                }}
              >
                <img
                  src={signature.image}
                  alt="Signed"
                  style={{ maxWidth: 220, maxHeight: 120, objectFit: "contain" }}
                />
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  size="sm"
                  color="outline-secondary"
                  onClick={() =>
                    setSignature({
                      isActive: true,
                      label: "Authorised Signatory",
                      image: null,
                    })
                  }
                >
                  Reset
                </Button>
              </div>
            </>
          ) : (
            <div
              style={{
                width: "100%",
                minHeight: 220,
                border: "2px dashed #dee2e6",
                borderRadius: 8,
                backgroundColor: "#f8f9fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalApproveVisible(false)}
                  onCloseCrop={() => setIsModalApproveVisible(true)}
                  onSave={(data: string) => {
                    setSignature((prev: any) => ({ ...prev, image: data }));
                  }}
                >
                  <Button color="primary" size="sm" outline style={{ minWidth: 140 }} onClick={() => setActiveSignatureContext("APPROVE")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </Button>
                </ImageUploadCropper>

                <Button
                  color="outline-primary"
                  size="sm"
                  style={{ minWidth: 140 }}
                  onClick={() => {
                    setIsModalApproveVisible(false);
                    setShowSignatureModel(true);
                  }}
                >
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal >

      <Modal
        title="Reject Work Request"
        visible={isModalRejectedVisible}
        onOk={openRejectConfirmSwal}
        onCancel={() => {
          handleCancel();
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical" name="rejectForm">
          <Form.Item
            name="reason"
            label="Reason for Rejection"
            rules={[{ required: true, message: "Please input your reason!" }]}
          >
            <Input.TextArea rows={3} placeholder="Type your reason here..." />
          </Form.Item>

          <div className="mt-4">
            <strong style={{ fontSize: "16px" }}>Signature</strong>
          </div>

          {signature.image && (
            <a
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() =>
                setSignature({ isActive: true, label: "Authorised Signatory", image: null })
              }
            >
            </a>
          )}


          {signature.image ? (
            <>
              <div className="signatueImgWrapper border mb-3 p-3 text-center">
                <img
                  src={signature.image}
                  alt="Signed"
                  style={{ width: "180px", height: "auto" }}
                />
              </div>
              <div className="d-flex justify-content-end mb-3">
                <Button
                  size="sm"
                  color="outline-secondary"
                  className="me-2"
                  onClick={() =>
                    setSignature({ isActive: true, label: "Authorised Signatory", image: null })
                  }
                >
                  Reset
                </Button>
              </div>

            </>
          ) : (
            <div
              style={{
                minHeight: "200px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "15px",
                border: "2px dashed #dee2e6",
                borderRadius: "8px",
                backgroundColor: "#f8f9fa"
              }}
            >
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalRejectedVisible(false)}
                  onCloseCrop={() => setIsModalRejectedVisible(true)}
                  onSave={(data: string) => {
                    setSignature((prev: any) => ({ ...prev, image: data }));
                  }}
                >
                  <Button color="primary" size="sm" outline onClick={() => setActiveSignatureContext("REJECT")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </Button>
                </ImageUploadCropper>

                <Button
                  color="outline-primary"
                  size="sm"
                  onClick={() => {
                    setIsModalRejectedVisible(false);
                    setShowSignatureModel(true);
                  }}
                >
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </Button>
              </div>
            </div>
          )}
        </Form>
      </Modal>

      {
        showSignatureModel && (
          <SignatureModal
            modal={showSignatureModel}
            toggle={() => {
              setShowSignatureModel(false);
              if (activeSignatureContext === "APPROVE") {
                setIsModalApproveVisible(true);
              } else if (activeSignatureContext === "REJECT") {
                setIsModalRejectedVisible(true);
              }
            }}
            onSave={(data: string) => {
              setSignature((prev: any) => ({ ...prev, image: data }));
              setShowSignatureModel(false);

              if (activeSignatureContext === "APPROVE") {
                setIsModalApproveVisible(true);
              } else if (activeSignatureContext === "REJECT") {
                setIsModalRejectedVisible(true);
              }
            }}
          />
        )
      }
    </>
  );
};

export default WorkRequestView;
